import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { mergeProps } from 'react-aria';

import { type NavigationHookProps, type NavigationResult, useNavigationProps } from '../Router';
import { CardProvider } from './Card';
import { type CardButtonHookProps, type CardButtonResult, useCardButton } from './CardButton';
import { CardDismissButton } from './CardDismissButton';

export type CardLinkHookProps = Omit<CardButtonHookProps, 'elementType'> & NavigationHookProps;

export type CardLinkResult = CardButtonResult & NavigationResult;

export type CardLinkProps = Omit<CardLinkHookProps, 'headingId' | 'descriptionId'> &
    Omit<React.ComponentPropsWithoutRef<'a'>, 'href'>;

/**
 * Card link component to use a card as a link.
 */
export const CardLink = React.forwardRef(function CardLink(
    {
        disabled,
        onPress,
        onPressStart,
        preventFocusOnPress,
        className,
        routerOptions,
        href,
        kind,
        ...props
    }: CardLinkProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const cardLinkProps = useCardLink({
        onPress,
        onPressStart,
        preventFocusOnPress,
        disabled,
        className,
        routerOptions,
        href,
        kind,
        download: props.download,
        target: props.target,
        onClick: props.onClick,
        headingId,
        descriptionId,
    });

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <a ref={ref} {...props} {...cardLinkProps}>
                {props.onDismiss ? <CardDismissButton onDismiss={props.onDismiss} /> : null}
                {props.children}
            </a>
        </CardProvider>
    );
});

/**
 * Low-level hook to create a card button component.
 */
export function useCardLink(props: CardLinkHookProps): CardLinkResult {
    const navigationProps = useNavigationProps(props);
    const cardButtonProps = useCardButton({ ...props, elementType: 'a' });
    return {
        ...mergeProps(navigationProps, cardButtonProps),
        // Remove default "a" tag styles
        className: clsx(cardButtonProps.className, 'text-base no-underline'),
    };
}
